import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { FormProvider, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Separator } from '~/components';
import { Button } from '~/components/Button';
import { FormFieldCombobox } from '~/components/FormField';
import { FormFieldRadioGroup } from '~/components/FormField/components/FormFieldRadioGroup';
import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { Drawer } from '~settings/components/Drawer';
import { UserStatus } from '~settings/components/Filters/UserStatus';
import { SelectLabelGroups } from '~settings/components/SelectLabelGroups';
import { SelectLabels } from '~settings/components/SelectLabels';
import { SelectUserRole } from '~settings/components/SelectUserRole';
import { SelectUserType } from '~settings/components/SelectUserType';
import { DEFAULT_FILTER_LIMIT } from '~settings/constants';
import {
  EXTERNAL_ROLES,
  INTERNAL_ROLES,
  USER_TYPES,
} from '~settings/constants/Filter';
import { useFilterForm } from '~settings/hooks/useFilterForm';
import type { UsersFilter } from '~settings/types/filter';

const oldDefaultValues: UsersFilter = {
  isActive: 'all',
  roles: [],
  labelIds: [],
  isInternal: 'all',
  labelGroupIds: [],
};

const newDefaultValues = {
  isActive: 'all',
  labelGroupIds: [],
  labelIds: [],
  roles: [],
  userTypes: [],
};

interface UsersFilterFormProps {
  onApply: () => void;
  open: boolean;
  options?: {
    showLabels?: boolean;
    showLabelGroups?: boolean;
  };
}

function mapToOptions(
  t: TFunction,
  options: { label: string; value: string }[],
) {
  return options.map((option) => ({
    label: t(option.label),
    value: option.value,
  }));
}

function OldFormFields({
  filterForm,
  showLabels,
  showLabelGroups,
}: {
  filterForm: UseFormReturn<UsersFilter>;
  showLabels?: boolean;
  showLabelGroups?: boolean;
}) {
  const { t } = useTranslation('settings');
  const isCurrentUserInternal = !useIsExternalUser();
  const [labelGroupsEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsLabelGroups,
  ]);

  const internalRoles = useMemo(() => mapToOptions(t, INTERNAL_ROLES), [t]);
  const externalRoles = useMemo(() => mapToOptions(t, EXTERNAL_ROLES), [t]);
  const userTypes = useMemo(() => mapToOptions(t, USER_TYPES), [t]);

  const internalFilter = String(filterForm.watch('isInternal'));
  const selectedExternal = internalFilter === 'false';
  const selectedInternal = internalFilter === 'true';

  const getRoleOptions = () => {
    if (!isCurrentUserInternal) {
      return externalRoles;
    }

    switch (internalFilter) {
      case 'true':
        return internalRoles;
      case 'false':
        return externalRoles;
      case 'all':
        return [...internalRoles, ...externalRoles];
      default:
        return [];
    }
  };

  return (
    <>
      <UserStatus name="isActive" />
      <Separator className="my-md" />
      {isCurrentUserInternal ? (
        <>
          <FormFieldRadioGroup
            name="isInternal"
            label={t('userType')}
            options={userTypes}
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value;

              filterForm.reset({
                ...filterForm.getValues(),
                isInternal: value,
                labelIds: [],
                labelGroupIds: [],
              });
            }}
          />
          <Separator className="my-md" />
        </>
      ) : null}
      <FormFieldCombobox
        name="roles"
        label={t('dropdowns.role.title', { count: 2 })}
        multiselect={true}
        displaySelectedOptions={true}
        options={getRoleOptions()}
        placeholder={t('dropdowns.role.placeholder', { count: 2 })}
        searchPlaceholder={t('dropdowns.role.searchPlaceholder', {
          count: 2,
        })}
      />
      {showLabels && !selectedInternal ? (
        <>
          <Separator className="my-md" />
          <SelectLabels
            maxSelectedOptions={DEFAULT_FILTER_LIMIT}
            name="labelIds"
            type="view"
          />
        </>
      ) : null}
      {showLabelGroups &&
      labelGroupsEnabled &&
      isCurrentUserInternal &&
      !selectedExternal ? (
        <>
          <Separator className="my-md" />
          <SelectLabelGroups
            maxSelectedOptions={DEFAULT_FILTER_LIMIT}
            name="labelGroupIds"
          />
        </>
      ) : null}
    </>
  );
}

OldFormFields.displayName = 'OldFormFields';

function isInternalSelected(userTypes: string[]): boolean {
  return userTypes.length !== 1 || !userTypes.includes('EXTERNAL');
}

function isExternalSelected(userTypes: string[]): boolean {
  return userTypes.length === 0 || userTypes.includes('EXTERNAL');
}

function UserFilterFields({
  filterForm,
  showLabels,
  showLabelGroups,
}: {
  filterForm: UseFormReturn<UsersFilter>;
  showLabels?: boolean;
  showLabelGroups?: boolean;
}) {
  const isCurrentUserInternal = !useIsExternalUser();
  const [labelGroupsEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsLabelGroups,
  ]);

  const userTypes = filterForm.watch('userTypes') ?? [];
  const selectedInternal = isInternalSelected(userTypes);
  const selectedExternal = isExternalSelected(userTypes);

  return (
    <>
      <UserStatus name="isActive" />
      <Separator className="my-md" />
      {isCurrentUserInternal ? (
        <>
          <SelectUserType
            name="userTypes"
            onChange={(value) => {
              let newUserType = Array.isArray(value) ? value : [value];

              filterForm.reset({
                ...filterForm.getValues(),
                userTypes: newUserType,
              });
            }}
            multiselect
          />
          <Separator className="my-md" />
        </>
      ) : null}
      <SelectUserRole name="roles" multiselect={true} />
      {showLabels && selectedExternal ? (
        <>
          <Separator className="my-md" />
          <SelectLabels
            maxSelectedOptions={DEFAULT_FILTER_LIMIT}
            name="labelIds"
            type="view"
          />
        </>
      ) : null}
      {showLabelGroups &&
      labelGroupsEnabled &&
      isCurrentUserInternal &&
      selectedInternal ? (
        <>
          <Separator className="my-md" />
          <SelectLabelGroups
            maxSelectedOptions={DEFAULT_FILTER_LIMIT}
            name="labelGroupIds"
          />
        </>
      ) : null}
    </>
  );
}

UserFilterFields.displayName = 'UserFilterFields';

export function UserFormFilter({
  open,
  onApply,
  options,
}: UsersFilterFormProps) {
  const { t } = useTranslation('settings');

  const [isInternalUserUpdatesEnabled] = useFeatureFlags([
    FEATURE_FLAGS.SettingsInternalUserUpdates,
  ]);

  const defaultValues = isInternalUserUpdatesEnabled
    ? newDefaultValues
    : oldDefaultValues;

  const { filterForm, handleApply, handleClear } = useFilterForm<UsersFilter>({
    onApply,
    isOpen: open,
    defaultValues,
  });

  return (
    <FormProvider {...filterForm}>
      <Drawer.Header>
        <Drawer.Title>{t('filters')}</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        {isInternalUserUpdatesEnabled ? (
          <UserFilterFields
            filterForm={filterForm}
            showLabels={options?.showLabels}
            showLabelGroups={options?.showLabelGroups}
          />
        ) : (
          <OldFormFields
            filterForm={filterForm}
            showLabels={options?.showLabels}
            showLabelGroups={options?.showLabelGroups}
          />
        )}
      </Drawer.Body>
      <Drawer.Footer>
        <Button
          size="large"
          variant="tertiary"
          onClick={handleClear}
          className="font-normal"
          type="button"
          data-testid="clear-filters-button"
        >
          {t('clear')}
        </Button>
        <Button
          size="large"
          type="button"
          onClick={filterForm.handleSubmit(handleApply)}
          data-testid="apply-filters-button"
        >
          {t('buttons.apply')}
        </Button>
      </Drawer.Footer>
    </FormProvider>
  );
}

UserFormFilter.displayName = 'UserFormFilter';
